import React from 'react';
import PropTypes from 'prop-types';
import './Annotation.scss';

/**
 * Annotation component for displaying markers on PDFs
 * Annotations are positioned absolutely over the PDF viewer
 * Details are shown in a fixed info panel on click
 * 
 * @param {Object} annotation - The annotation object
 * @param {string} annotation.id - Unique identifier for the annotation
 * @param {number} annotation.x - X position (percentage, 0-100)
 * @param {number} annotation.y - Y position (percentage, 0-100)
 * @param {number} annotation.number - The badge number to display (1-4)
 * @param {string} annotation.text - The explanation text to display
 * @param {string} annotation.title - Optional title for the annotation
 * @param {string} [annotation.annotationType] - Display type: 'data', 'table', or 'media'
 * @param {boolean} [annotation.visible] - Whether annotation is visible (default: true)
 * @param {Function} [onSelect] - Callback when annotation marker is clicked
 */
const Annotation = ({ annotation, onSelect }) => {
  if (!annotation.visible) return null;

  const { id, x, y, number = 1, annotationType = 'data' } = annotation;

  const handleClick = () => {
    if (onSelect) {
      onSelect(annotation);
    }
  };

  return (
    <div
      className="annotation"
      style={{
        left: `${x}%`,
        top: `${y}%`,
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {/* Marker/indicator on the PDF */}
      <div 
        className="annotation__marker"
        data-number={number}
      >
        {number}
      </div>
    </div>
  );
};

Annotation.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    number: PropTypes.number.isRequired,
    text: PropTypes.string,
    title: PropTypes.string,
    annotationType: PropTypes.oneOf(['data', 'table', 'media']),
    visible: PropTypes.bool,
  }).isRequired,
  onSelect: PropTypes.func,
};

export default Annotation;
